import { useState } from 'react'
import { Delete } from 'lucide-react'
import { cn } from '@/lib/utils'
import { usePOSPreferencesStore } from '@/lib/store/usePOSPreferencesStore'

const KEYBOARD_ROWS = [
  ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'],
  ['Q', 'W', 'E', 'R', 'T', 'Y', 'U', 'I', 'O', 'P'],
  ['A', 'S', 'D', 'F', 'G', 'H', 'J', 'K', 'L'],
  ['Z', 'X', 'C', 'V', 'B', 'N', 'M', '.', ','],
]

type NumpadMode = 'integer' | 'decimal'

function cleanNumberInput(value: string, mode: NumpadMode, allowNegative: boolean) {
  let next = value.replace(/[^\d.-]/g, '')
  if (!allowNegative) next = next.replace(/-/g, '')
  if (allowNegative) {
    next = next.replace(/(?!^)-/g, '')
  }
  if (mode === 'integer') {
    next = next.replace(/\./g, '')
  } else {
    const [head, ...tail] = next.split('.')
    next = tail.length > 0 ? `${head}.${tail.join('')}` : head
  }
  return next
}

function appendNumberToken(value: string, token: string, mode: NumpadMode, allowNegative: boolean) {
  if (token === '.') {
    if (mode === 'integer' || value.includes('.')) return value || '0.'
    return value === '' || value === '-' ? `${value}0.` : `${value}.`
  }
  if (token === '-') {
    if (!allowNegative) return value
    return value.startsWith('-') ? value.slice(1) : `-${value}`
  }
  if (value === '0') return token
  if (value === '-0') return `-${token}`
  return cleanNumberInput(`${value}${token}`, mode, allowNegative)
}

export function POSVisualNumpad({
  value,
  onChange,
  mode = 'decimal',
  allowNegative = false,
  quickValues = [],
  className,
}: {
  value: string
  onChange: (value: string) => void
  mode?: NumpadMode
  allowNegative?: boolean
  quickValues?: { label: string; value: string }[]
  className?: string
}) {
  const keys = ['7', '8', '9', '4', '5', '6', '1', '2', '3', mode === 'decimal' ? '.' : '00', '0', 'backspace']

  return (
    <div className={cn('space-y-2 rounded-[var(--radius-md)] border border-[var(--color-border-subtle)] bg-[var(--color-bg-base)] p-2', className)}>
      {quickValues.length > 0 && (
        <div className="grid grid-cols-3 gap-1.5">
          {quickValues.map((quick) => (
            <button
              key={`${quick.label}:${quick.value}`}
              type="button"
              onClick={() => onChange(cleanNumberInput(quick.value, mode, allowNegative))}
              className="h-8 rounded-[var(--radius-sm)] border border-[var(--color-border-subtle)] bg-[var(--color-bg-elevated)] px-2 text-xs font-semibold text-[var(--color-text-secondary)] transition-colors hover:border-[var(--color-border-default)] hover:text-[var(--color-text-primary)]"
            >
              {quick.label}
            </button>
          ))}
        </div>
      )}
      <div className="grid grid-cols-3 gap-1.5">
        {keys.map((key) => (
          <button
            key={key}
            type="button"
            onClick={() => {
              if (key === 'backspace') {
                onChange(value.slice(0, -1))
                return
              }
              onChange(appendNumberToken(value, key, mode, allowNegative))
            }}
            className="flex h-10 items-center justify-center rounded-[var(--radius-sm)] border border-[var(--color-border-subtle)] bg-[var(--color-bg-elevated)] text-sm font-bold text-[var(--color-text-primary)] transition-colors hover:border-[var(--color-border-default)] active:scale-[0.98]"
            aria-label={key === 'backspace' ? 'Delete last digit' : `Enter ${key}`}
          >
            {key === 'backspace' ? <Delete className="h-4 w-4" /> : key}
          </button>
        ))}
      </div>
      <div className="grid grid-cols-2 gap-1.5">
        {allowNegative && (
          <button
            type="button"
            onClick={() => onChange(appendNumberToken(value, '-', mode, allowNegative))}
            className="h-8 rounded-[var(--radius-sm)] border border-[var(--color-border-subtle)] bg-[var(--color-bg-elevated)] text-xs font-semibold text-[var(--color-text-secondary)] transition-colors hover:border-[var(--color-border-default)] hover:text-[var(--color-text-primary)]"
          >
            +/-
          </button>
        )}
        <button
          type="button"
          onClick={() => onChange('')}
          className={cn(
            'h-8 rounded-[var(--radius-sm)] border border-[var(--color-border-subtle)] bg-[var(--color-bg-elevated)] text-xs font-semibold text-[var(--color-text-secondary)] transition-colors hover:border-[var(--color-border-default)] hover:text-[var(--color-text-primary)]',
            !allowNegative && 'col-span-2',
          )}
        >
          Clear
        </button>
      </div>
    </div>
  )
}

export function POSVisualNumpadField({
  label,
  value,
  onChange,
  placeholder = '0',
  mode = 'decimal',
  allowNegative = false,
  prefix,
  quickValues,
  className,
  inputClassName,
}: {
  label?: string
  value: string
  onChange: (value: string) => void
  placeholder?: string
  mode?: NumpadMode
  allowNegative?: boolean
  prefix?: string
  quickValues?: { label: string; value: string }[]
  className?: string
  inputClassName?: string
}) {
  const [open, setOpen] = useState(false)
  const inputMode = usePOSPreferencesStore((state) => state.inputMode)

  if (inputMode === 'laptop') {
    return (
      <div className={cn('space-y-2', className)}>
        {label && <label className="block text-xs font-medium text-[var(--color-text-secondary)]">{label}</label>}
        <div className={cn('flex h-10 w-full items-center gap-2 rounded-[var(--radius-md)] border border-[var(--color-border-default)] bg-[var(--color-bg-elevated)] px-3 focus-within:border-[var(--color-accent-amber)] focus-within:ring-1 focus-within:ring-[var(--color-accent-amber)]/30', inputClassName)}>
          {prefix && <span className="text-xs font-semibold text-[var(--color-text-muted)]">{prefix}</span>}
          <input
            type="text"
            inputMode={mode === 'integer' ? 'numeric' : 'decimal'}
            value={value}
            onChange={(event) => onChange(cleanNumberInput(event.target.value, mode, allowNegative))}
            placeholder={placeholder}
            className="min-w-0 flex-1 bg-transparent text-right font-mono text-base text-[var(--color-text-primary)] outline-none placeholder:text-[var(--color-text-muted)]"
          />
        </div>
      </div>
    )
  }

  return (
    <div className={cn('space-y-2', className)}>
      {label && <label className="block text-xs font-medium text-[var(--color-text-secondary)]">{label}</label>}
      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        className={cn(
          'flex h-10 w-full items-center justify-between gap-2 rounded-[var(--radius-md)] border border-[var(--color-border-default)] bg-[var(--color-bg-elevated)] px-3 text-sm text-[var(--color-text-primary)] transition-colors hover:border-[var(--color-border-strong)]',
          open && 'border-[var(--color-accent-amber)] ring-1 ring-[var(--color-accent-amber)]/30',
          inputClassName,
        )}
      >
        {prefix && <span className="text-xs font-semibold text-[var(--color-text-muted)]">{prefix}</span>}
        <span className="ml-auto font-mono text-base">{value || placeholder}</span>
      </button>
      {open && (
        <POSVisualNumpad
          value={value}
          onChange={(next) => onChange(cleanNumberInput(next, mode, allowNegative))}
          mode={mode}
          allowNegative={allowNegative}
          quickValues={quickValues}
        />
      )}
    </div>
  )
}

export function POSVisualKeyboard({
  value,
  onChange,
  className,
}: {
  value: string
  onChange: (value: string) => void
  className?: string
}) {
  const [shift, setShift] = useState(false)

  function insert(token: string) {
    onChange(`${value}${shift ? token : token.toLowerCase()}`)
  }

  return (
    <div className={cn('space-y-1.5 rounded-[var(--radius-md)] border border-[var(--color-border-subtle)] bg-[var(--color-bg-base)] p-2', className)} aria-label="Visual keyboard">
      {KEYBOARD_ROWS.map((row) => (
        <div key={row.join('')} className="flex gap-1.5">
          {row.map((key) => (
            <button
              key={key}
              type="button"
              onClick={() => insert(key)}
              className="flex h-8 min-w-0 flex-1 items-center justify-center rounded-[var(--radius-sm)] border border-[var(--color-border-subtle)] bg-[var(--color-bg-elevated)] text-xs font-semibold text-[var(--color-text-primary)] transition-colors hover:border-[var(--color-border-default)]"
            >
              {shift ? key : key.toLowerCase()}
            </button>
          ))}
        </div>
      ))}
      <div className="flex gap-1.5">
        <button
          type="button"
          onClick={() => setShift((current) => !current)}
          className={cn(
            'flex h-8 flex-1 items-center justify-center rounded-[var(--radius-sm)] border text-xs font-semibold transition-colors hover:border-[var(--color-border-default)]',
            shift
              ? 'border-[var(--color-accent-amber)] bg-[var(--color-accent-amber)]/10 text-[var(--color-accent-amber)]'
              : 'border-[var(--color-border-subtle)] bg-[var(--color-bg-elevated)] text-[var(--color-text-secondary)]',
          )}
        >
          Shift
        </button>
        <button
          type="button"
          onClick={() => onChange(`${value} `)}
          className="flex h-8 flex-[2] items-center justify-center rounded-[var(--radius-sm)] border border-[var(--color-border-subtle)] bg-[var(--color-bg-elevated)] text-xs font-semibold text-[var(--color-text-primary)] transition-colors hover:border-[var(--color-border-default)]"
        >
          Space
        </button>
        <button
          type="button"
          onClick={() => onChange(value.slice(0, -1))}
          aria-label="Delete last character"
          className="flex h-8 flex-1 items-center justify-center rounded-[var(--radius-sm)] border border-[var(--color-border-subtle)] bg-[var(--color-bg-elevated)] text-[var(--color-text-secondary)] transition-colors hover:border-[var(--color-border-default)] hover:text-[var(--color-text-primary)]"
        >
          <Delete className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          onClick={() => onChange('')}
          className="flex h-8 flex-1 items-center justify-center rounded-[var(--radius-sm)] border border-[var(--color-border-subtle)] bg-[var(--color-bg-elevated)] text-xs font-semibold text-[var(--color-text-secondary)] transition-colors hover:border-[var(--color-border-default)] hover:text-[var(--color-text-primary)]"
        >
          Clear
        </button>
      </div>
    </div>
  )
}

export function POSVisualKeyboardField({
  label,
  value,
  onChange,
  placeholder,
  multiline = false,
  rows = 2,
  className,
  inputClassName,
}: {
  label?: string
  value: string
  onChange: (value: string) => void
  placeholder?: string
  multiline?: boolean
  rows?: number
  className?: string
  inputClassName?: string
}) {
  const [open, setOpen] = useState(false)
  const inputMode = usePOSPreferencesStore((state) => state.inputMode)
  const commonClassName = cn(
    'w-full rounded-[var(--radius-sm)] border border-[var(--color-border-default)] bg-[var(--color-bg-elevated)] px-3 py-2 text-sm text-[var(--color-text-primary)] placeholder:text-[var(--color-text-muted)] focus:outline-none',
    open && 'border-[var(--color-accent-amber)] ring-1 ring-[var(--color-accent-amber)]/30',
    inputClassName,
  )

  return (
    <div className={cn('space-y-2', className)}>
      {label && <label className="block text-xs font-medium text-[var(--color-text-secondary)]">{label}</label>}
      {multiline ? (
        <textarea
          readOnly={inputMode === 'touchscreen'}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          onClick={() => inputMode === 'touchscreen' && setOpen((current) => !current)}
          placeholder={placeholder}
          rows={rows}
          className={cn(commonClassName, 'resize-none')}
        />
      ) : (
        <input
          readOnly={inputMode === 'touchscreen'}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          onClick={() => inputMode === 'touchscreen' && setOpen((current) => !current)}
          placeholder={placeholder}
          className={cn(commonClassName, 'h-9')}
        />
      )}
      {inputMode === 'touchscreen' && open && <POSVisualKeyboard value={value} onChange={onChange} />}
    </div>
  )
}
